import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { mainSiteUrl, getAppBasePath, auth0Audience } from '../config/auth0';

/**
 * Primary authentication hook for the application.
 * Provides user info, authentication state, and auth actions.
 */
export const useAuth = () => {
  const {
    isAuthenticated,
    isLoading,
    user,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
    error,
  } = useAuth0();

  // User properties
  const email = user?.email;
  const userId = user?.sub;
  const name = user?.name || user?.email;
  const picture = user?.picture;

  /**
   * Attempt silent authentication (SSO check).
   * If user is logged into stokastic.com, they'll be auto-authenticated.
   */
  const checkSSO = useCallback(async () => {
    try {
      await getAccessTokenSilently({
        authorizationParams: {
          prompt: 'none', // Silent - no UI
        },
      });
      return true;
    } catch (error) {
      // No existing session - user needs to log in
      return false;
    }
  }, [getAccessTokenSilently]);

  /**
   * Redirect to Auth0 login.
   * Users with an existing stokastic.com session will be auto-authenticated.
   */
  const login = useCallback((returnTo?: string) => {
    // Capture full path including search params
    const currentPath = returnTo || window.location.pathname + window.location.search;
    loginWithRedirect({
      appState: { returnTo: currentPath },
    });
  }, [loginWithRedirect]);

  /**
   * Redirect to Auth0 signup page.
   * Uses screen_hint to show the signup form instead of login.
   */
  const signup = useCallback((returnTo?: string) => {
    const currentPath = returnTo || window.location.pathname + window.location.search;
    loginWithRedirect({
      appState: { returnTo: currentPath },
      authorizationParams: {
        screen_hint: 'signup',
      },
    });
  }, [loginWithRedirect]);

  /**
   * Redirect to main site for login (alternative flow).
   * Use this if you want users to always log in via stokastic.com.
   */
  const loginViaMainSite = useCallback((returnTo?: string) => {
    const returnUrl = returnTo || window.location.href;
    window.location.href = `${mainSiteUrl}/api/auth/login?returnTo=${encodeURIComponent(returnUrl)}`;
  }, []);

  /**
   * Log out from Auth0.
   * This logs out from the Auth0 session (affects all apps using this tenant).
   * Note: returnTo URL must be in Auth0's "Allowed Logout URLs" list.
   * We save the current path to localStorage so we can redirect back after logout.
   */
  const logout = useCallback((returnTo?: string) => {
    // Save current path for redirect after logout
    const currentPath = window.location.pathname + window.location.search;
    localStorage.setItem('stokastic_post_logout_redirect', currentPath);

    auth0Logout({
      logoutParams: {
        returnTo: returnTo || window.location.origin + getAppBasePath(),
      },
    });
  }, [auth0Logout]);

  /**
   * Get access token for API calls.
   * Returns null if not authenticated.
   * Requests a JWT token with the API audience for cross-origin verification.
   */
  const getToken = useCallback(async (): Promise<string | null> => {
    if (!isAuthenticated) {
      return null;
    }
    try {
      return await getAccessTokenSilently({
        authorizationParams: {
          audience: auth0Audience,
        },
      });
    } catch (error) {
      console.error('Failed to get access token:', error);
      return null;
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  return {
    // State
    isAuthenticated,
    isLoading,
    error,

    // User info
    user,
    email,
    userId,
    name,
    picture,

    // Actions
    login,
    signup,
    loginViaMainSite,
    logout,
    getToken,
    checkSSO,
  };
};

/**
 * Hook to get just the user ID (for backward compatibility).
 */
export const useUserId = (): string | undefined => {
  const { userId } = useAuth();
  return userId;
};

/**
 * Hook to get just the user email.
 */
export const useUserEmail = (): string | undefined => {
  const { email } = useAuth();
  return email;
};
